import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { formatAmount, formatDate, formatPhone, withCountRu } from '@dorixona/utils'
import { ExportButton } from '../../ui/ExportButton'
import { Icon } from '../../ui/Icon'
import { Failed } from '../../ui/Load'
import { TableSkeleton } from '../../ui/Skeleton'
import { Pagination } from '../../ui/Pagination'
import { PageActions } from '../../app/PageHeader'
import { Select, type SelectOption } from '../../ui/Select'
import { initials } from '../auth/model'
import { useCustomers, useExportCustomers } from './api'
import {
  EMPTY_FILTERS,
  PAGE_SIZES,
  deletedText,
  displayName,
  filtersFromParams,
  filtersToParams,
  genderText,
  isDeleted,
  isFiltered,
  notifyText,
  type CustomerFilters,
  type CustomerStatus,
  type PageSize,
} from './model'

const STATUSES: SelectOption<CustomerStatus>[] = [
  { value: '', label: 'Все клиенты' },
  { value: 'active', label: 'Активные' },
  { value: 'blocked', label: 'Заблокированные' },
  { value: 'deleted', label: 'Удалённые' },
]

export interface CustomersScreenProps {
  onToast: (message: string) => void
}

/**
 * Клиенты программы лояльности. Поиск, фильтр по статусу и листание делает
 * сервер: реестр целиком в браузер не выгружается.
 */
export function CustomersScreen({ onToast }: CustomersScreenProps) {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const filters = filtersFromParams(params)
  const query = { q: filters.q.trim(), status: filters.status }
  const customers = useCustomers({ ...query, page: filters.page, pageSize: filters.size })
  const exportCustomers = useExportCustomers()

  /**
   * Новый отбор начинается с первой страницы. Листание — отдельный шаг в
   * истории браузера, а правка поиска заменяет текущий: иначе каждая буква
   * стала бы шагом «Назад».
   */
  function set(patch: Partial<CustomerFilters>) {
    const paging = 'page' in patch
    setParams(filtersToParams({ ...filters, page: 1, ...patch }), { replace: !paging })
  }

  const filtered = isFiltered(filters)

  function reset() {
    set({ ...EMPTY_FILTERS, size: filters.size })
  }

  function goTo(page: number) {
    set({ page })
    // Новая страница читается сверху: таблица под фильтрами.
    window.scrollTo({ top: 0 })
  }

  return (
    <>
      <PageActions>
        {customers.data ? (
          <span
            className="ui-count adm-count"
            aria-label={withCountRu(customers.data.total, ['клиент', 'клиента', 'клиентов'])}
          >
            {customers.data.total}
          </span>
        ) : null}
        <span className="spacer" />
        <ExportButton prefix="klienty" load={() => exportCustomers(query)} onError={onToast} />
      </PageActions>

      <div className="ui-toolbar" role="search">
        <div className="ui-search">
          <Icon name="i-search" />
          <input
            className="ui-input"
            type="search"
            placeholder="Имя или телефон"
            aria-label="Поиск клиента"
            value={filters.q}
            onChange={(e) => {
              set({ q: e.target.value })
            }}
          />
        </div>
        <Select
          label="Статус клиента"
          value={filters.status}
          active={filters.status !== EMPTY_FILTERS.status}
          options={STATUSES}
          onChange={(status) => {
            set({ status })
          }}
        />
        {filtered ? (
          <button type="button" className="ui-btn" data-variant="ghost" onClick={reset}>
            Сбросить фильтры
          </button>
        ) : null}
      </div>

      <section className="ui-table-card" aria-label="Клиенты">
        {customers.isPending ? <TableSkeleton what="клиентов" /> : null}
        {customers.isError ? (
          <Failed
            what="клиентов"
            error={customers.error}
            onRetry={() => {
              void customers.refetch()
            }}
          />
        ) : null}

        {customers.data ? (
          <>
            <div className="ui-table-wrap" aria-busy={customers.isPlaceholderData}>
              <table className="ui-table ui-customers">
                <thead>
                  <tr>
                    <th scope="col">Клиент</th>
                    <th scope="col">Телефон</th>
                    <th scope="col">Пол</th>
                    <th scope="col">Зарегистрирован</th>
                    <th scope="col" data-align="end">
                      Баланс баллов
                    </th>
                    <th scope="col">Статус</th>
                    <th scope="col">Уведомления</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.data.items.length === 0 ? (
                    <tr>
                      <td colSpan={7}>
                        <div className="ui-empty">
                          <Icon name="i-search" />
                          <b>Клиенты не найдены</b>
                          <p>
                            {filters.page > 1
                              ? `На странице ${filters.page} клиентов нет — список стал короче.`
                              : filtered
                                ? 'Измените запрос или сбросьте фильтры.'
                                : 'Клиенты появятся, когда зарегистрируются в приложении.'}
                          </p>
                          {filtered ? (
                            <button
                              type="button"
                              className="ui-btn"
                              data-size="sm"
                              data-variant="secondary"
                              onClick={reset}
                            >
                              Сбросить фильтры
                            </button>
                          ) : null}
                          {filters.page > 1 ? (
                            <button
                              type="button"
                              className="ui-btn"
                              data-size="sm"
                              data-variant="secondary"
                              onClick={() => {
                                goTo(1)
                              }}
                            >
                              К первой странице
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ) : null}
                  {customers.data.items.map((customer) => {
                    const href = `/customers/${customer.id}`
                    const deleted = isDeleted(customer)
                    const name = displayName(customer)
                    return (
                      <tr
                        key={customer.id}
                        data-href={href}
                        data-muted={deleted || undefined}
                        onClick={() => {
                          void navigate(href)
                        }}
                      >
                        <td>
                          <div className="ui-person">
                            <span className="ui-avatar" aria-hidden="true">
                              {initials(name)}
                            </span>
                            <span className="txt">
                              <Link
                                to={href}
                                className="primary"
                                onClick={(event) => {
                                  event.stopPropagation()
                                }}
                              >
                                {name}
                              </Link>
                              <span className="secondary">
                                {customer.anonymizedAt
                                  ? 'Данные стёрты'
                                  : customer.telegram
                                    ? 'Telegram'
                                    : 'приложение'}
                              </span>
                            </span>
                          </div>
                        </td>
                        <td className="num">{formatPhone(customer.phone)}</td>
                        <td>{genderText(customer.gender)}</td>
                        <td className="num cus-muted">
                          {customer.registered ? formatDate(customer.registered) : '—'}
                        </td>
                        {/* Баллы — обычное число: жёлтый в админке только у очереди. */}
                        <td data-align="end" className="cus-balance">
                          {formatAmount(customer.balance)}
                        </td>
                        <td>
                          {/* Заблокированный не войдёт в приложение (BR-06) — это
                              состояние, поэтому статус, а не метка. Удаление
                              важнее блокировки: войти уже некому. */}
                          {deleted ? (
                            <span className="ui-status" data-tone="neutral">
                              {deletedText(customer)}
                            </span>
                          ) : customer.blocked ? (
                            <span className="ui-status" data-tone="danger">
                              заблокирован
                            </span>
                          ) : (
                            <span className="ui-status" data-tone="success">
                              активен
                            </span>
                          )}
                        </td>
                        <td className="cus-muted">{notifyText(customer.notifyPush)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <Pagination
              label="Страницы клиентов"
              page={filters.page}
              pageSize={filters.size}
              total={customers.data.total}
              sizes={PAGE_SIZES}
              busy={customers.isPlaceholderData}
              onPage={goTo}
              onPageSize={(size) => {
                set({ size: size as PageSize })
              }}
            />
          </>
        ) : null}
      </section>
    </>
  )
}
